import React, { useMemo, useState } from 'react';
import { minorUnits, money, toMajor } from '../../../utils/money';
import { Btn, DangerBtn, DataTable, Loading, Msg, Section, TextInput, fmtDate, panelGet, panelPost, useAdminGet } from '../kit';
import { Fact, Pill, S, list, num, rowsOf, str } from './shared';
import { usdPrice } from '../../../landing-page/components/ipintel/usd';

/**
 * Pricing — the IP Intel price per region, kept apart from the Voyager
 * membership prices on purpose. A visitor's country (their billing country
 * when signed in, else the IP's) is matched against each ACTIVE region's
 * country list; no match means "pricing on request" and a Contact us button.
 * India is the only region today; more are added here, no deploy needed.
 *
 * GET ip_intel/prices · POST ip_intel/price_save · POST ip_intel/price_toggle,
 * all audited. "USD price shown" (POST ip_intel/usd_display_save) is the
 * USD figure printed beside the INR price — display only, never charged. The preview is GET ip_intel/preview?country=XX — the same
 * config_for() the public page's GET /api/v1/ip-intel/config answers with, so
 * it shows exactly what that visitor's page would get.
 */

type Region = {
  region_code: string; label: string; countries: string[]; currency: string; amount_minor: number;
  tax_rate_pct: number; tax_label: string; active: boolean; sort: number;
  updated_by?: string; updated_at?: string;
  /** The server's own rounding of base + tax, when it sends it. */
  total_minor?: number;
};

type Draft = {
  region_code: string; label: string; countries: string; currency: string; amount: string;
  tax_rate_pct: string; tax_label: string; active: boolean; sort: string; isNew: boolean;
};

const NEW_DRAFT: Draft = {
  region_code: '', label: '', countries: '', currency: 'INR', amount: '', tax_rate_pct: '18',
  tax_label: 'GST', active: false, sort: '', isNew: true,
};

const normRegion = (r: any): Region => ({
  region_code: str(r.region_code ?? r.code).toUpperCase(),
  label: str(r.label),
  countries: list(r.countries).map(c => c.toUpperCase()),
  currency: (str(r.currency) || 'INR').toUpperCase(),
  amount_minor: num(r.amount_minor),
  tax_rate_pct: num(r.tax_rate_pct),
  tax_label: str(r.tax_label),
  active: r.active === true || r.active === 'true' || r.active === 1,
  sort: num(r.sort),
  updated_by: str(r.updated_by),
  updated_at: str(r.updated_at),
  total_minor: r.total_minor === undefined || r.total_minor === null ? undefined : num(r.total_minor),
});

const taxOf = (baseMinor: number, pct: number) => Math.round(baseMinor * pct / 100);

const parseCountries = (s: string) =>
  Array.from(new Set(s.split(/[\s,;]+/).map(x => x.trim().toUpperCase()).filter(Boolean)));

const Pricing: React.FC = () => {
  const [tick, setTick] = useState(0);
  const res = useAdminGet(S, 'prices', {}, [tick]);
  const note = str(res.data?.note);
  const regions: Region[] = useMemo(() =>
    rowsOf(res.data, 'regions', 'prices').map(normRegion).sort((a, b) => a.sort - b.sort || a.region_code.localeCompare(b.region_code)),
  [res.data]);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  // ---- the USD figure shown beside the INR price (display only) ----
  const usdNow = usdPrice(res.data?.usd || null);
  const usdDefaults = usdPrice(res.data?.usd_defaults || null);
  const [usdDraft, setUsdDraft] = useState<{ price: string; note: string } | null>(null);
  const [usdBusy, setUsdBusy] = useState(false);
  const usdErr = useMemo(() => {
    if (!usdDraft) return '';
    const t = usdDraft.price.trim();
    if (!/^\d{1,7}$/.test(t) || Number(t) < 1 || Number(t) > 1_000_000) return 'USD price: a whole number of dollars, 1 to 1,000,000.';
    if (usdDraft.note.trim().length > 40) return 'Note: at most 40 characters.';
    if (/[<>]/.test(usdDraft.note)) return 'Note: no < or > characters.';
    return '';
  }, [usdDraft]);
  const saveUsd = async () => {
    if (!usdDraft || usdErr) return;
    setUsdBusy(true); setMsg(null);
    const { error } = await panelPost(S, 'usd_display_save', {
      usd_price: Number(usdDraft.price.trim()), usd_note: usdDraft.note.trim(),
    });
    setUsdBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setMsg({ kind: 'ok', text: 'USD price saved — the page and the order modals show it on their next load. Checkout stays in INR.' });
    setUsdDraft(null);
    setTick(t => t + 1);
  };

  const [cc, setCc] = useState('IN');
  const [preview, setPreview] = useState<{ cc: string; data: any; error: string } | null>(null);
  const [pvBusy, setPvBusy] = useState(false);

  const edit = (r: Region) => {
    setMsg(null);
    setDraft({
      region_code: r.region_code, label: r.label, countries: r.countries.join(', '), currency: r.currency,
      amount: String(toMajor(r.amount_minor, r.currency)), tax_rate_pct: String(r.tax_rate_pct),
      tax_label: r.tax_label, active: r.active, sort: String(r.sort), isNew: false,
    });
  };
  const addNew = () => {
    setMsg(null);
    setDraft({ ...NEW_DRAFT, sort: String((regions.reduce((m, r) => Math.max(m, r.sort), 0) || 0) + 10) });
  };

  // ---- validation (the server re-validates; this keeps the obvious out) ----
  const v = useMemo(() => {
    if (!draft) return null;
    const errs: string[] = [];
    const warns: string[] = [];
    const code = draft.region_code.trim().toUpperCase();
    const cur = draft.currency.trim().toUpperCase();
    const countries = parseCountries(draft.countries);
    const amount = Number(draft.amount);
    const pct = Number(draft.tax_rate_pct);
    if (!/^[A-Z0-9_]{2,16}$/.test(code)) errs.push('Region code: 2–16 letters, digits or _ (e.g. IN, EU, US).');
    if (draft.isNew && regions.some(r => r.region_code === code)) errs.push(`Region ${code} already exists — edit it instead.`);
    if (!draft.label.trim()) errs.push('Give the region a label (shown to visitors).');
    if (!countries.length) errs.push('List at least one country (ISO-2 codes, comma-separated).');
    const bad = countries.filter(c => !/^[A-Z]{2}$/.test(c));
    if (bad.length) errs.push(`Not ISO-2 country codes: ${bad.join(', ')}.`);
    if (!/^[A-Z]{3}$/.test(cur)) errs.push('Currency: a 3-letter ISO code (INR, USD, EUR…).');
    if (!(amount > 0)) errs.push('Price must be more than zero.');
    if (!(pct >= 0 && pct <= 50) || draft.tax_rate_pct.trim() === '') errs.push('Tax % must be between 0 and 50.');
    if (pct > 0 && !draft.tax_label.trim()) errs.push('Name the tax (GST, VAT…) — it is printed next to the price.');
    const minor = Math.round(amount * minorUnits(cur));
    if (amount > 0 && Math.abs(minor / minorUnits(cur) - amount) > 1e-9) {
      warns.push(`${cur} has ${Math.log10(minorUnits(cur))} decimal places — the price is rounded to ${money(minor, cur)}.`);
    }
    if (draft.active) {
      const clash = regions.filter(r => r.active && r.region_code !== code)
        .map(r => ({ r, both: r.countries.filter(c => countries.includes(c)) }))
        .filter(x => x.both.length);
      clash.forEach(x => errs.push(`${x.both.join(', ')} ${x.both.length === 1 ? 'is' : 'are'} already priced by active region ${x.r.region_code} — a country can be in one active region only.`));
    }
    if (cur !== 'INR') warns.push('Check Razorpay accepts one-time payments in this currency on the live account before activating.');
    return { errs, warns, code, cur, countries, minor, pct };
  }, [draft, regions]);

  const save = async () => {
    if (!draft || !v || v.errs.length) return;
    setBusy(true); setMsg(null);
    const { error } = await panelPost(S, 'price_save', {
      region_code: v.code, label: draft.label.trim(), countries: v.countries, currency: v.cur,
      amount_minor: v.minor, tax_rate_pct: v.pct, tax_label: draft.tax_label.trim(),
      active: draft.active, sort: Number(draft.sort) || 0, is_new: draft.isNew,
    });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setMsg({ kind: 'ok', text: `${v.code} saved — the page shows the new price on its next load. Orders already created keep theirs.` });
    setDraft(null);
    setTick(t => t + 1);
  };

  const toggle = async (r: Region) => {
    setMsg(null);
    const { error } = await panelPost(S, 'price_toggle', { region_code: r.region_code, active: !r.active });
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setMsg({ kind: 'ok', text: r.active
      ? `${r.region_code} deactivated — visitors from ${r.countries.join(', ')} now see “pricing on request”.`
      : `${r.region_code} is live for ${r.countries.join(', ')}.` });
    setTick(t => t + 1);
  };

  const runPreview = async (code: string) => {
    const c = code.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(c)) { setPreview({ cc: c, data: null, error: 'Type a 2-letter country code, e.g. IN or US.' }); return; }
    setCc(c); setPvBusy(true); setPreview(null);
    const { data, error } = await panelGet(S, 'preview', { country: c });
    setPvBusy(false);
    setPreview({ cc: c, data, error });
  };

  const activeCount = regions.filter(r => r.active).length;
  const rows = regions.map(r => ({
    ...r,
    price: money(r.amount_minor, r.currency),
    tax: `${r.tax_rate_pct}% ${r.tax_label}`.trim(),
    total: money(r.total_minor ?? (r.amount_minor + taxOf(r.amount_minor, r.tax_rate_pct)), r.currency),
  }));

  const pd = preview?.data || null;
  const quick = Array.from(new Set([...regions.filter(r => r.active).map(r => r.countries[0]).filter(Boolean), 'US', 'GB']));

  return (
    <div className="flex flex-col gap-4">
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <Section title="Regions & prices"
        right={
          <div className="flex items-center gap-2">
            <span className="text-xs text-grayscale-1100">{activeCount} active of {regions.length}</span>
            <Btn small onClick={() => setTick(t => t + 1)} disabled={res.loading}>Refresh</Btn>
            <Btn small kind="primary" onClick={addNew} disabled={!!draft}>Add region</Btn>
          </div>
        }>
        <p className="text-xs text-grayscale-1100">
          Separate from the Voyager membership prices. The page, the payment sheet and the Razorpay order all
          read these. {note || 'A visitor whose country is in no active region sees “Pricing for your region on request”.'}
        </p>
        {res.error && <Msg kind="error">{res.error}</Msg>}
        {res.loading && !res.data ? <Loading /> : (
          <DataTable rows={rows}
            columns={['region_code', 'label', 'countries', 'price', 'tax', 'total', 'active', 'updated_at']}
            labels={{ region_code: 'Region', label: 'Label', countries: 'Countries', price: 'Price', tax: 'Tax',
              total: 'Total paid', active: 'Status', updated_at: 'Updated' }}
            empty="No regions yet — add one. Until then nobody can order."
            render={{
              region_code: r => <span className="font-mono font-bold">{r.region_code}</span>,
              countries: r => <span className="text-xs" title={r.countries.join(', ')}>{r.countries.join(', ') || '—'}</span>,
              active: r => r.active ? <Pill tone="good">Live</Pill> : <Pill tone="neutral">Off</Pill>,
              updated_at: r => (
                <span className="text-xs" title={r.updated_by || undefined}>
                  {r.updated_at ? fmtDate(r.updated_at) : '—'}{r.updated_by ? ` · ${r.updated_by}` : ''}
                </span>
              ),
            }}
            actions={r => (
              <span className="flex gap-1.5">
                <Btn small onClick={() => edit(r as Region)} disabled={!!draft && draft.region_code !== r.region_code}>Edit</Btn>
                {r.active
                  ? <DangerBtn small onConfirm={() => toggle(r as Region)}>Deactivate</DangerBtn>
                  : <Btn small onClick={() => toggle(r as Region)}>Activate</Btn>}
                <Btn small onClick={() => runPreview((r as Region).countries[0] || '')}
                  disabled={!(r as Region).countries.length}>Preview</Btn>
              </span>
            )} />
        )}
      </Section>

      <Section title="USD price shown (display only)"
        right={usdDraft ? null : <Btn small onClick={() => { setMsg(null); setUsdDraft({ price: String(usdNow.price), note: usdNow.note }); }}
          disabled={res.loading && !res.data}>Edit</Btn>}>
        <p className="text-xs text-grayscale-1100">
          Printed beside the INR price on the IP Intel page, its payment sheet, the tech-card order modal and the
          IIT Bombay page, e.g. “₹40,000 + GST · or {usdNow.label}”. Nothing is charged in USD: checkout and
          Razorpay stay in INR. Default {usdDefaults.label}.
        </p>
        {usdDraft ? (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
              <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
                USD price (whole dollars)
                <TextInput type="number" value={usdDraft.price} onChange={x => setUsdDraft({ ...usdDraft, price: x })} placeholder="500" />
              </label>
              <label className="flex flex-col gap-1 text-xs text-grayscale-1100 sm:col-span-2">
                Note after it (may be empty)
                <TextInput wide value={usdDraft.note} onChange={x => setUsdDraft({ ...usdDraft, note: x.slice(0, 40) })} placeholder="no GST" />
              </label>
            </div>
            <Fact label="Visitors see" value={usdErr ? '' : `₹40,000 + GST · or ${usdPrice({ usd_price: Number(usdDraft.price), usd_note: usdDraft.note }).label}`} />
            {usdErr && <Msg kind="error">{usdErr}</Msg>}
            <div className="flex gap-2 items-center">
              <Btn kind="primary" onClick={saveUsd} disabled={usdBusy || !!usdErr}>{usdBusy ? 'Saving…' : 'Save USD price'}</Btn>
              <Btn onClick={() => setUsdDraft(null)}>Cancel</Btn>
              <Btn small onClick={() => setUsdDraft({ price: String(usdDefaults.price), note: usdDefaults.note })}>Reset to default</Btn>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 max-w-md">
            <Fact label="USD price" value={usdNow.amount} />
            <Fact label="Note" value={usdNow.note || '(none)'} />
          </div>
        )}
      </Section>

      {draft && v && (
        <Section title={draft.isNew ? 'New region' : `Edit ${draft.region_code}`}
          right={<Btn small onClick={() => { setDraft(null); setMsg(null); }}>Close</Btn>}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
              Region code
              {draft.isNew
                ? <TextInput value={draft.region_code} onChange={x => setDraft({ ...draft, region_code: x.toUpperCase() })} placeholder="e.g. US" />
                : <span className="h-9 flex items-center font-mono font-bold text-sm text-grayscale-1300">{draft.region_code}</span>}
            </label>
            <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
              Label (shown to visitors)
              <TextInput value={draft.label} onChange={x => setDraft({ ...draft, label: x })} placeholder="e.g. United States" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-grayscale-1100 sm:col-span-2">
              Countries (ISO-2, comma-separated)
              <TextInput wide value={draft.countries} onChange={x => setDraft({ ...draft, countries: x.toUpperCase() })} placeholder="IN" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
              Currency
              <TextInput value={draft.currency} onChange={x => setDraft({ ...draft, currency: x.toUpperCase().slice(0, 3) })} placeholder="INR" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
              Price excl. tax ({v.cur || '—'})
              <TextInput type="number" value={draft.amount} onChange={x => setDraft({ ...draft, amount: x })} placeholder="40000" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
              Tax %
              <TextInput type="number" value={draft.tax_rate_pct} onChange={x => setDraft({ ...draft, tax_rate_pct: x })} placeholder="18" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
              Tax label
              <TextInput value={draft.tax_label} onChange={x => setDraft({ ...draft, tax_label: x })} placeholder="GST" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
              Sort order
              <TextInput type="number" value={draft.sort} onChange={x => setDraft({ ...draft, sort: x })} placeholder="10" />
            </label>
            <label className="flex items-center gap-2 text-sm text-grayscale-1300 sm:col-span-3 pt-5">
              <input type="checkbox" checked={draft.active} onChange={e => setDraft({ ...draft, active: e.target.checked })} />
              Live — visitors from these countries can order at this price
            </label>
          </div>

          <div className="grid grid-cols-3 gap-2 max-w-xl">
            <Fact label="Visitors see" value={v.minor > 0 && /^[A-Z]{3}$/.test(v.cur)
              ? `${money(v.minor, v.cur)}${v.pct > 0 ? ` + ${draft.tax_label.trim() || 'tax'}` : ''}` : ''} />
            <Fact label={`${draft.tax_label.trim() || 'Tax'} ${v.pct || 0}%`}
              value={v.minor > 0 && /^[A-Z]{3}$/.test(v.cur) ? money(taxOf(v.minor, v.pct || 0), v.cur) : ''} />
            <Fact label="Charged at Razorpay" value={v.minor > 0 && /^[A-Z]{3}$/.test(v.cur)
              ? <b>{money(v.minor + taxOf(v.minor, v.pct || 0), v.cur)}</b> : ''} />
          </div>

          {v.errs.length > 0 && (
            <Msg kind="error">
              <ul className="list-disc pl-4">{v.errs.map(e => <li key={e}>{e}</li>)}</ul>
            </Msg>
          )}
          {v.warns.length > 0 && (
            <div className="text-sm rounded-lg px-3 py-2 border text-amber-700 bg-amber-50 border-amber-200">
              <ul className="list-disc pl-4">{v.warns.map(e => <li key={e}>{e}</li>)}</ul>
            </div>
          )}
          <div className="flex gap-2 items-center">
            <Btn kind="primary" onClick={save} disabled={busy || v.errs.length > 0}>
              {busy ? 'Saving…' : draft.isNew ? 'Create region' : 'Save changes'}
            </Btn>
            <Btn onClick={() => { setDraft(null); setMsg(null); }}>Cancel</Btn>
            <span className="text-[11px] text-grayscale-1100">
              Takes effect on the live page at once. Orders already created keep the price they were made at.
            </span>
          </div>
        </Section>
      )}

      <Section title="Preview — what a visitor from a country sees">
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={cc} onChange={x => setCc(x.toUpperCase().slice(0, 2))} placeholder="IN" />
          <Btn kind="primary" small onClick={() => runPreview(cc)} disabled={pvBusy}>{pvBusy ? 'Checking…' : 'Preview'}</Btn>
          <span className="text-xs text-grayscale-1100">quick:</span>
          {quick.map(q => <Btn key={q} small onClick={() => runPreview(q)} disabled={pvBusy}>{q}</Btn>)}
        </div>
        {preview?.error && <Msg kind="error">{preview.error}</Msg>}
        {pd && (
          pd.available === false ? (
            <div className="rounded-lg border border-grayscale-300 bg-grayscale-100 px-4 py-3 text-sm flex flex-col gap-1">
              <div><b>{preview?.cc}</b> is in no active region.</div>
              <div className="text-grayscale-1200">
                The page shows “Pricing for your region on request” and the order buttons become
                “Contact us” (e-mail {str(pd.contact_email) || 'info@universitytech.io'}).
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Fact label="Region" value={[pd.region?.code, pd.region?.label].filter(Boolean).join(' · ')} />
                <Fact label="Price line" value={str(pd.display?.price_with_tax_label) || money(num(pd.base_minor), str(pd.currency) || 'INR')} />
                <Fact label={`${str(pd.tax_label) || 'Tax'} ${pd.tax_rate_pct ?? ''}%`} value={money(num(pd.tax_minor), str(pd.currency) || 'INR')} />
                <Fact label="Pay button total" value={<b>{str(pd.display?.total) || money(num(pd.total_minor), str(pd.currency) || 'INR')}</b>} />
              </div>
              <div className="text-[11px] text-grayscale-1100">
                Turnaround {pd.turnaround_days ?? 7} days · {rowsOf(pd.samples).length} sample report{rowsOf(pd.samples).length === 1 ? '' : 's'} listed
                · Razorpay key {pd.razorpay_key_id ? <span className="font-mono">{str(pd.razorpay_key_id).slice(0, 12)}…</span> : 'missing'}
                {str(pd.razorpay_key_id).startsWith('rzp_test') && <b className="text-amber-700"> (test mode)</b>}
              </div>
              {pd.payments_enabled === false && (
                <Msg kind="error">Payments are off: the server has no Razorpay key, so the Pay button cannot open checkout.</Msg>
              )}
            </div>
          )
        )}
      </Section>
    </div>
  );
};

export default Pricing;
